import React, {
  useEffect, useState, useCallback, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  LoadingPage, BASE_UI_URL, apiExploreCountries, apiGetProfileInfo,
  NotificationBanner, apiSaveWishList, apiGetStamps, DEFAULT_MAX_RETRY,
  ImagePrefetcher, apiUpdatePassportBanner,
} from '@americanexpress/arcade-core';
import { addErrorToReport } from '@americanexpress/one-app-ducks';
import { fromJS } from 'immutable';
import { useReactlytics } from '@americanexpress/reactlytics';
import { MODULE_NAME, STAMPS_SCAN_STATUS } from '../constants';
import styles from './styles/renderArcadeChildren.scss';
import {
  apiGetPassportBanner,
  apiProfileRefresh,
  apiPollStampsScanStatus,
} from '../api';
import NavBar from './NavBar';
import SurveyPopUp from './SurveyPopUp';

const renderLink = ({ chunks, onClick }) => (
  <button
    className={`btn btn-tertiary pad-0 text-underline ${styles.link}`}
    type="button"
    onClick={onClick}
  >
    {chunks}
  </button>
);

const RenderArcadeChildren = ({
  isMobile,
  isNative,
  children,
  router,
  showNavbar,
  pathname,
}) => {
  const isStampDetailsCalled = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'isStampDetailsCalled']));
  const userStatus = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'userStatus']));
  const isFullScreenError = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'isFullScreenError']));
  const isWishlistError = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'wishlistError', 'isError']));
  const wishlistRetryCount = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'wishlistError', 'retryCount']));
  const isStampsListApiError = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'isStampsListApiError']));
  const stampListApiRetryCount = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'stampListApiRetryCount']));
  const sourcePage = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'wishlistError', 'unsavedPayload', 'sourcePage']));
  const wishlistCountryCodes = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'wishlistError', 'unsavedPayload', 'wishlistCountryCodes']));
  const previouslyWishlistedCountryCodes = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'wishlistError', 'unsavedPayload', 'previouslyWishlistedCountryCodes']));
  const canProfileRefresh = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'canProfileRefresh']));
  const showScanBanner = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'showScanBanner']));
  const stampsScanStatus = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'stampsScanStatus']));
  const isUserUnenrolled = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'isUserUnenrolled']));
  const fetchedUrlModuleNames = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'fetchedUrlModuleNames']))?.toArray() || [];
  const isCountryListLoading = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'isCountryListLoading']));
  const isCountryListApiError = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'isCountryListApiError']));
  const isWishlistSaving = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'isWishlistSaving']));
  const gemUrl = useSelector((state) => state.getIn(['config', 'GemUrl']));
  const dispatch = useDispatch();
  const { track } = useReactlytics();
  const { search, query, hash } = router.location;
  const canReadLocalStorage = typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
  const prevUserStatus = canReadLocalStorage
    ? JSON.parse(window.localStorage.getItem('prevUserStatus') || 'null')
    : null;
  const isFirstTimeUser = userStatus === 'enrolled' ? prevUserStatus?.isFirstTimeUser ?? true : true;
  const [isSurveyPopUpOpen, setIsSurveyPopUpOpen] = useState(false);
  const [surveyPromptStatus, setSurveyPromptStatus] = useState(false);
  const surveyPromptHandledRef = useRef(false);
  const countryListApiInitiatedRef = useRef(false);
  const wishlistSaveInitiatedRef = useRef(false);
  const intl = useIntl();

  useEffect(() => {
    const handleRouteToLanding = () => {
      if (query?.faq === 'true') {
        router.replace(`${BASE_UI_URL}/landing/#faq`);
      } else if (hash !== '#faq') {
        /* checking #faq as this function was
        running twice and hence removing the #faq */
        router.replace(`${BASE_UI_URL}/landing`);
      }
    };

    if (userStatus && isMobile) {
      switch (userStatus) {
        case 'unenrolled':
        case 'not_enrolled':
          dispatch(apiGetProfileInfo({
            collectionMethod: 'apiGetProfileInfo - Exception',
            moduleID: 'arcade-page-wrapper',
          }));
          if (pathname.includes('/enroll')) {
            const targetSlide = `${BASE_UI_URL}/enroll${search}`;
            router.replace(targetSlide);
          } else if (isNative || isUserUnenrolled) {
            handleRouteToLanding();
          } else {
            router.replace(`${BASE_UI_URL}/enroll`);
          }
          break;
        case 'enrolled':
          dispatch(apiExploreCountries({
            collectionMethod: 'apiExploreCountries - Exception',
            moduleID: 'arcade-page-wrapper',
          }));
          countryListApiInitiatedRef.current = true;
          dispatch(apiGetStamps({
            collectionMethod: 'apiGetStamps - Exception',
            moduleID: 'arcade-page-wrapper',
          }));
          if (query?.faq === 'true') {
            router.replace(`${BASE_UI_URL}/landing/#faq`);
          }
          break;
        case 'ineligible':
          router.replace(`${BASE_UI_URL}/landing`);
          break;
        case 'unauthorized':
          handleRouteToLanding();
          break;
        /* istanbul ignore next -- default case */
        default:
          // router.push('/');
          break;
      }
    }

    if (!isMobile) {
      handleRouteToLanding();
    }
  }, [userStatus, isMobile]);

  useEffect(() => {
    if (isUserUnenrolled) {
      router.replace(`${BASE_UI_URL}`);
    }
  }, [isUserUnenrolled]);

  useEffect(() => {
    if (!isCountryListLoading && !isCountryListApiError && countryListApiInitiatedRef.current) {
      const unsavedWishlist = JSON.parse(localStorage.getItem('unsavedWishlist'));
      if (unsavedWishlist) {
        // update UI state
        dispatch({
          type: 'modules/arcade-root/SET_WISHLISTED_COUNTRIES',
          payload: {
            wishlistCountryCodes: unsavedWishlist.wishlistCountryCodes,
            previouslyWishlistedCountryCodes: unsavedWishlist.previouslyWishlistedCountryCodes,
          },
        });

        dispatch(apiSaveWishList({
          wishlistCountryCodes: unsavedWishlist.wishlistCountryCodes,
          previouslyWishlistedCountryCodes: unsavedWishlist.previouslyWishlistedCountryCodes,
          sourcePage: unsavedWishlist.sourcePage,
          log: { collectionMethod: 'apiSaveWishList - Exception', moduleID: 'arcade-page-wrapper' },
        }));
      }
    }
  }, [isCountryListLoading, isCountryListApiError, dispatch]);

  useEffect(() => {
    if (isWishlistSaving) {
      wishlistSaveInitiatedRef.current = true;
    }

    if (!isWishlistSaving && wishlistSaveInitiatedRef.current) {
      localStorage.removeItem('unsavedWishlist');
      wishlistSaveInitiatedRef.current = false;
    }
  }, [isWishlistSaving]);

  useEffect(() => {
    const subPath = pathname.replace(BASE_UI_URL, '') || '/';
    const shouldRedirectToStamps = subPath === '/'
      || (subPath === '/landing' && hash !== '#faq')
      || subPath === '/enroll';
    let enrolledUserPath;
    const match = subPath.match(/^\/(.+)\/report$/);

    if (match && !isStampDetailsCalled) {
      enrolledUserPath = `${BASE_UI_URL}/${match[1]}`;
    } else {
      enrolledUserPath = shouldRedirectToStamps ? `${BASE_UI_URL}/stamps` : `${BASE_UI_URL}${subPath}${search}${hash}`;
    }

    if (userStatus === 'enrolled' && !canProfileRefresh && isMobile) {
      if (enrolledUserPath !== `${pathname}${search}${hash}`) {
        router.replace(enrolledUserPath);
      }
    }

    if (canProfileRefresh) {
      dispatch(apiProfileRefresh());
    }
  }, [userStatus, canProfileRefresh, isMobile, pathname]);

  useEffect(() => {
    if (userStatus && userStatus === 'enrolled') {
      dispatch(apiGetPassportBanner((status) => {
        setSurveyPromptStatus(status);
        if (status) surveyPromptHandledRef.current = false;
      }));
    }
  }, [userStatus]);

  const timeoutRef = React.useRef();

  useEffect(() => {
    const popUpIsAvailable = !isFullScreenError
      && !pathname.includes('unenroll')
      && !pathname.includes('enroll')
      && !pathname.includes('landing')
      && !pathname.includes('feedback');

    if (surveyPromptHandledRef.current || !surveyPromptStatus) return;

    if (!isFirstTimeUser && popUpIsAvailable) {
      if (!timeoutRef.current) {
        timeoutRef.current = setTimeout(() => {
          setIsSurveyPopUpOpen(true);
          surveyPromptHandledRef.current = true;
        }, 30000);
      }
    } else if (!isFirstTimeUser && !popUpIsAvailable) {
      clearTimeout(timeoutRef.current);
      surveyPromptHandledRef.current = true;
    }
  }, [isFirstTimeUser, pathname, isFullScreenError, surveyPromptStatus]);

  const submitSurveyPrompt = useCallback(async () => {
    const maxAttempts = 3;
    let responseData;

    for (let i = 0; i < maxAttempts; i += 1) {
      // eslint-disable-next-line no-await-in-loop -- need to retry on failure
      responseData = await dispatch(apiUpdatePassportBanner({
        payload: {
          is_feedback_prompted: true,
          operation: 'feedback_prompt',
        },
        log: {
          collectionMethod: 'apiSubmitSurveyPrompt - Exception',
          moduleID: 'arcade-page-wrapper',
        },
      }));

      if (responseData?.status_code === 'success') {
        break;
      }
    }

    if (responseData?.status_code !== 'success') {
      dispatch(addErrorToReport(new Error('Max attempts reached.'), {
        collectionMethod: 'apiSubmitSurveyPrompt - Exception',
        moduleID: 'arcade-page-wrapper',
      }));
    }
  }, [dispatch]);

  useEffect(() => {
    if (isSurveyPopUpOpen) {
      submitSurveyPrompt();
      surveyPromptHandledRef.current = true;
    }
  }, [isSurveyPopUpOpen, submitSurveyPrompt]);

  const handleTryAgain = () => {
    track({
      event: {
        identifier: 'axp-passport-wishlist-save-error',
        element: `try-again:${sourcePage}`,
        type: 'click',
      },
    });
    dispatch(apiSaveWishList({
      wishlistCountryCodes: wishlistCountryCodes.toJS(),
      previouslyWishlistedCountryCodes: previouslyWishlistedCountryCodes.toJS(),
      sourcePage,
      log: { collectionMethod: 'apiSaveWishList - Exception', moduleID: 'arcade-page-wrapper' },
    }));
  };

  const handleDismissBanner = () => {
    track({
      event: {
        identifier: 'axp-passport-wishlist-save-error',
        element: `x-but:${sourcePage}`,
        type: 'click',
      },
    });
    dispatch({
      type: 'modules/arcade-root/SET_WISHLIST_ERROR',
      payload: fromJS({
        isError: false,
        retryCount: 0,
        unsavedPayload: {
          wishlistCountryCodes,
          previouslyWishlistedCountryCodes,
          sourcePage,
        },
      }),
    });
  };

  useEffect(() => {
    if (isWishlistError) {
      track({
        event: {
          identifier: 'axp-passport-wishlist-save-error',
          element: `wishlist-error:${sourcePage}`,
          type: 'impression',
        },
      });
    }
  }, [isWishlistError, sourcePage]);

  const refreshStampsList = useCallback(() => {
    track({
      event: {
        identifier: 'axp-passport-stamp-load-error',
        element: 'refresh',
        type: 'click',
      },
    });
    dispatch(apiGetStamps({
      collectionMethod: 'apiGetStamps - Exception',
      moduleID: 'arcade-page-wrapper',
    }));
  }, [dispatch, track]);

  useEffect(() => {
    if (isStampsListApiError) {
      track({
        event: {
          identifier: 'axp-passport-stamp-home-error',
          element: 'load-error',
          type: 'impression',
        },
      });
    }
  }, [isStampsListApiError]);

  useEffect(() => {
    if (stampsScanStatus === STAMPS_SCAN_STATUS.IN_PROGRESS) {
      dispatch(apiPollStampsScanStatus());
    }
  }, [stampsScanStatus]);

  const handleRefresh = useCallback(() => {
    track({
      event: {
        identifier: 'axp-passport-new-stamp-msg',
        element: 'refresh',
        type: 'click',
      },
    });
    router.replace(`${BASE_UI_URL}/stamps`);
    refreshStampsList();
  }, [router, track, refreshStampsList]);

  const handleDismissScanBanner = () => {
    track({
      event: {
        identifier: 'axp-passport-fins-re-try',
        element: 'x-but',
        type: 'click',
      },
    });
    dispatch({
      type: 'modules/arcade-root/SET_SHOW_SCAN_BANNER',
      payload: false,
    });
  };

  const getRefreshBannerMessage = useCallback(() => {
    if (stampsScanStatus === STAMPS_SCAN_STATUS.IN_PROGRESS) {
      track({
        event: {
          identifier: 'axp-passport-feed-scan-msg',
          element: 'stamp-scan',
          type: 'impression',
        },
      });
      return intl.formatMessage({ id: 'stamp.banner.new' });
    }

    if (stampsScanStatus === STAMPS_SCAN_STATUS.COMPLETED) {
      track({
        event: {
          identifier: 'axp-passport-feed-new-stamp-msg',
          element: 'new-stamp-ref',
          type: 'impression',
        },
      });
      return (
        <FormattedMessage
          id="stamp.banner.refresh"
          values={{
            link: (chunks) => renderLink({ chunks, onClick: handleRefresh }),
          }}
        />
      );
    }

    track({
      event: {
        identifier: 'axp-passport-feed',
        element: 'fins-re-try',
        type: 'impression',
      },
    });
    return intl.formatMessage({ id: 'stamp.banner.error' });
  }, [stampsScanStatus, intl, track, handleRefresh]);

  // Prefetch images for share module used in overview and explore pages
  const prefetchImagesUrlArr = [
    `${gemUrl}/Images/shareBackground.png`,
    `${gemUrl}/Images/explore-layer.png`,
  ];

  const shouldPrefetch = userStatus === 'enrolled' && !fetchedUrlModuleNames.includes(MODULE_NAME);

  const prefetcher = shouldPrefetch && (
    <ImagePrefetcher
      imagesUrls={prefetchImagesUrlArr}
      onPrefetchExecuted={() => {
        dispatch({
          type: 'modules/arcade-root/PREFETCHED_MODULE_URLS',
          payload: MODULE_NAME,
        });
      }}
    />
  );

  const isBot = global.BROWSER && /bot|crawl|slurp|spider|mediapartners/i.test(navigator.userAgent);
  if ((!userStatus && !isBot) || canProfileRefresh) {
    return <LoadingPage />;
  }

  return (
    <main
      data-testid="arcade-main"
      className="height-full width-full"
    >
      {prefetcher}
      {showScanBanner && userStatus === 'enrolled'
        && (
          <NotificationBanner
            type="neutral"
            isNative={isNative}
            className={`${stampsScanStatus !== STAMPS_SCAN_STATUS.ERROR ? styles.hideDismissButton : ''}`}
            onDismiss={handleDismissScanBanner}
          >
            {getRefreshBannerMessage()}
          </NotificationBanner>
        )}
      {isStampsListApiError && userStatus === 'enrolled' && (
        <NotificationBanner
          isNative={isNative}
          type="warning"
          dismissable={false}
          className={`${styles.errorBanner} ${stampListApiRetryCount >= DEFAULT_MAX_RETRY ? '' : styles.hideDismissButton}`}
        >
          {stampListApiRetryCount >= DEFAULT_MAX_RETRY
            ? <FormattedMessage id="max.retry" />
            : (
              <FormattedMessage
                id="stamp.banner.isStampsListApiError"
                values={{
                  link: (chunks) => renderLink({
                    chunks,
                    onClick: refreshStampsList,
                  }),
                }}
              />
            )}
        </NotificationBanner>
      )}
      {isWishlistError && userStatus === 'enrolled' && (
        <NotificationBanner
          isNative={isNative}
          type="warning"
          className={styles.errorBanner}
          onDismiss={handleDismissBanner}
        >
          {wishlistRetryCount >= DEFAULT_MAX_RETRY
            ? <FormattedMessage id="max.retry" />
            : (
              <FormattedMessage
                id="wishlist.error"
                values={{
                  link: (chunks) => renderLink({ chunks, onClick: handleTryAgain }),
                }}
              />
            )}
        </NotificationBanner>
      )}
      {isSurveyPopUpOpen
        && (
          <SurveyPopUp
            setIsSurveyPopUpOpen={setIsSurveyPopUpOpen}
            router={router}
          />
        )}
      {children}
      {showNavbar && isMobile && userStatus !== 'ineligible'
        && (
          <NavBar
            pathname={pathname}
          />
        )}
    </main>
  );
};

RenderArcadeChildren.propTypes = {
  isMobile: PropTypes.bool,
  isNative: PropTypes.bool,
  children: PropTypes.element,
  router: PropTypes.shape({
    push: PropTypes.func,
    replace: PropTypes.func,
    location: PropTypes.PropTypes.shape({
      search: PropTypes.string,
      query: PropTypes.shape({
        faq: PropTypes.string,
      }),
      hash: PropTypes.string,
    }),
  }).isRequired,
  showNavbar: PropTypes.bool.isRequired,
  pathname: PropTypes.string.isRequired,
};

export default RenderArcadeChildren;
